import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { errMsg, api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const AuthForm = () => {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [sent, setSent] = useState(false);
  const up = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const go = (m) => { setMode(m); setErr(""); setSent(false); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email: f.email });
        setSent(true);
        return;
      }
      const u = mode === "login" ? await login(f.email, f.password) : await register(f.name, f.email, f.password);
      toast.success(mode === "login" ? `Welcome back${u.name ? `, ${u.name}` : ""}.` : "Your profile is ready.");
    } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="w-full max-w-md border border-[var(--gold)]/40 bg-[var(--crypt)] p-8 sm:p-10 hex-clip">
      <div className="grid grid-cols-2 border border-[var(--line)]">
        {[["login", "Log in"], ["register", "Create profile"]].map(([m, l]) => (
          <button key={m} type="button" data-testid={`auth-tab-${m}`} onClick={() => go(m)}
            className={`py-3 font-mono text-xs uppercase tracking-[0.2em] transition-colors ${mode === m ? "bg-[var(--blood)] text-[var(--ink)]" : "text-[var(--ink-dim)] hover:text-[var(--gold)]"}`}>
            {l}
          </button>
        ))}
      </div>
      <h1 className="font-display text-3xl font-bold mt-8">{mode === "login" ? "Enter the court" : mode === "register" ? "Join the court" : "Forgot password"}</h1>
      <p className="text-sm text-[var(--ink-dim)] mt-2">
        {mode === "login" ? "Log in to see your requests and profile." : mode === "register" ? "Create a profile to track every commission you send." : "Enter your email and we'll send you a link to set a new password."}
      </p>
      {sent ? (
        <div data-testid="forgot-sent-msg" className="mt-6 border border-[var(--gold)]/40 p-5 text-sm text-[var(--gold-soft)] leading-relaxed">
          If an account exists for {f.email}, a reset link is on its way. It expires in 1 hour, so check your inbox (and spam folder).
        </div>
      ) : (
      <form onSubmit={submit} data-testid="auth-form" className="mt-6 space-y-3">
        {mode === "register" && (
          <input required data-testid="auth-name-input" className="field" placeholder="Your name" value={f.name} onChange={up("name")} />
        )}
        <input required type="email" data-testid="auth-email-input" className="field" placeholder="Email" value={f.email} onChange={up("email")} />
        {mode !== "forgot" && <input required type="password" minLength={mode === "register" ? 8 : undefined} data-testid="auth-password-input" className="field"
          placeholder={mode === "register" ? "Password (8+ characters)" : "Password"} value={f.password} onChange={up("password")} />}
        {mode === "login" && (
          <button type="button" data-testid="forgot-password-link" onClick={() => go("forgot")} className="text-sm text-[var(--gold)] hover:text-[var(--gold-soft)] underline underline-offset-4">
            Forgot password?
          </button>
        )}
        {err && <p data-testid="auth-error" className="text-sm text-[var(--blood)]">{err}</p>}
        <button disabled={busy} data-testid="auth-submit-btn" className="btn-blood w-full py-4 font-mono text-sm tracking-[0.2em] uppercase disabled:opacity-60">
          {busy ? "One moment…" : mode === "login" ? "Log in" : mode === "register" ? "Create profile" : "Send reset link"}
        </button>
      </form>
      )}
      {mode === "forgot" && (
        <button type="button" data-testid="forgot-back-btn" onClick={() => go("login")} className="mt-6 text-sm text-[var(--ink-dim)] hover:text-[var(--gold)]">
          ← Back to log in
        </button>
      )}
    </motion.div>
  );
};
